<template>
  <div
    class="cg-input"
    :class="{ 'cg-input--filter': showFilterStyles }"
    :style="styleVariables"
  >
    <label v-if="showLabel" :for="elementId">
      <!-- @slot Adds input label. -->
      <slot name="label" />
    </label>

    <div
      :aria-invalid="hasError"
      aria-describedby="errmsg"
      class="cg-input__content"
      @click="focusInput()"
    >
      <!-- @slot Adds an item before input. -->
      <slot name="prepend-icon" />
      <!-- v-on="$listeners" override and handle input -->
      <input
        :type="type"
        :value="lazyValue"
        v-bind="$attrs"
        :id="elementId"
        :placeholder="placeholder"
        @focus="onFocus"
        @blur="onBlur"
        @input="onInput"
      >

      <!-- @slot Adds an item after input. -->
      <slot name="append-icon">
        <cg-icon-close-circle
          v-if="showClearIcon"
          :height="22"
          :width="22"
          class="cg-input__clear-icon"
          @click.native.stop="clearValue"
        />
      </slot>
    </div>

    <div v-if="showDetails">
      <div v-if="showHintMessage" class="cg-input__hint-message">
        <!-- @slot Adds an item with hint styles. -->
        <slot name="hint-message" />
      </div>
      <div v-if="showErrorMessage" class="cg-input__error-message" id="errmsg">
        <!-- @slot Adds an item with error styles. -->
        <slot name="error-message">
          {{ getFirstErrorMessage }}
        </slot>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { PropType } from 'vue';
import ElementId from '../../mixins/elementId';
import ValidationMethod from '@corporategift/design-system/validations/ValidationMethod';

// @ts-ignore
import { CgIconCloseCircle } from '../../icons'

type IInputSlots = {
  'prepend-icon'?: unknown;
  'append-icon'?: unknown;
  'hint-message'?: unknown;
  'error-message'?: unknown;
  'label'?: unknown;
}

export default ElementId.extend({
  name: 'CgInput',
  components: {
    CgIconCloseCircle,
  },
  props: {
    /**
     * The input’s value
     * @model
     */
    value: {
      required: false,
      default: null,
    },
    /**
     * Sets input type
     */
    type: {
      type: String,
      required: false,
      default: 'text',
    },
    /**
     * Sets the DOM id on the component
     */
    id: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Add input clear functionality.
     */
    clearable: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Hides hint and validation errors.
     */
    hideDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Sets the height of the input
     */
    height: {
      type: Number,
      required: false,
      default: 40,
    },
    /**
     * Sets the max-width of the input
     */
    width: {
      type: Number,
      required: false,
      default: null,
    },
    /**
     * Adds active styles when value is not empty
     */
    filter: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Sets the input’s placeholder text
     */
    placeholder: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Array of functions
     * @see list of available validations
     */
    validation: {
      type: Array as PropType<Array<ValidationMethod>>,
      required: false,
      default: () => ([]),
    },
    /**
     * Delays validation until blur event
     */
    validateOnBlur: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Puts the input in a manual error state
     */
    error: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => ({
    lazyValue: null as any,
    inputIsValid: true,
    isFocused: false,
    errorMessages: [] as Array<string>,
  }),
  computed: {
    modelValue: {
      get(): string | number | null { return this.lazyValue; },
      set(val: string | number | null) {
        this.lazyValue = val;
        /**
         * Emitted when the v-model is changed.
         * 
         * @property {any} value new value
         */
        this.$emit('input', this.lazyValue);
      }
    },
    styleVariables(): Object {
      const { height, width } = this;

      return {
        '--input-height': `${height}px`,
        '--input-width': width ? `${width}px` : undefined,
      };
    },
    getFirstErrorMessage(): string | null {
      const { errorMessages } = this

      return errorMessages.length > 0 ? errorMessages[0] : null;
    },
    hasInternalError (): boolean {
      return this.errorMessages.length > 0 || !this.inputIsValid;
    },
    hasError (): boolean {
      return this.hasInternalError || this.error;
    },
    slots(): IInputSlots {
      return this.$slots;
    },
    showLabel(): boolean {
      return !!this.slots?.label;
    },
    showDetails(): boolean {
      const { slots, errorMessages, hideDetails } = this;

      if (hideDetails) return false;

      return !!(slots?.['hint-message'] || slots?.['error-message'] || errorMessages.length);
    },
    showHintMessage(): boolean {
      return !!this.slots?.['hint-message'];
    },
    showErrorMessage(): boolean {
      return !!(this.slots?.['error-message'] || this.errorMessages.length);
    },
    showFilterStyles(): boolean {
      const { modelValue, filter } = this;

      return !!(filter && modelValue);
    },
    showClearIcon(): boolean {
      const { modelValue, clearable } = this

      return clearable && !!modelValue
    },
  },
  watch: {
    value (val: any) {
      this.lazyValue = val;
    },
    hasInternalError (val: boolean) {
      /**
       * The `error.sync` event - returns boolean
       * 
       * @property {boolean} val
       */
      this.$emit('update:error', val);
    },
  },
  mounted() {
    this.lazyValue = this.value;
  },
  methods: {
    onFocus(e: FocusEvent): void {
      this.isFocused = true;
      /**
       * Emitted when component is focused
       * 
       * @property {FocusEvent} Event
       */
      this.$emit('focus', e);
    },
    onBlur(e: FocusEvent): void {
      this.isFocused = false;
      if (this.validateOnBlur) { this.validate(); }

      /**
       * Emitted when the input is blurred
       * 
       * @property {FocusEvent} Event
       */
      this.$emit('blur', e);
    },
    onInput(e: InputEvent): void {
      const target = e.target as HTMLInputElement;
      this.modelValue = target.value;
      if (!this.validateOnBlur) { this.validate(); }
    },
    /**
     * Focus input.
     * @public
     */
    focusInput(): void {
      const input = this.$el?.querySelector('input');
      if (input && !this.isFocused) { input.focus(); }
    },
    /**
     * Validates input. Returns **true** if successful and **false** if not.
     * @public
     */
    validate(): boolean {
      const value = this.lazyValue;
      const errors: Array<string | boolean> = [];

      (this.validation as Array<ValidationMethod>).forEach((fnc) => {
        const valid = fnc(value);

        if (valid === false || typeof valid === 'string') {
          errors.push(valid || '');
        } else if (typeof valid !== 'boolean') {
          console.error(`Validation should return a string or boolean, received '${typeof valid}' instead`);
        }
      });

      this.inputIsValid = errors.length === 0;
      this.errorMessages = (errors?.filter((item) => ((typeof item === 'string') && item)) ?? []) as Array<string>;

      return !this.hasError;
    },
    /**
     * Resets validation.
     * @public
     */
    resetValidation(): void {
      this.inputIsValid = true;
      this.errorMessages = [];
    },
    clearValue(e: Event): void {
      /**
       * Emitted when clearable icon clicked.
       * 
       * @property {Event} event click event
       */
      this.$emit('click:clear', e);

      this.modelValue = null;
      this.resetValidation();
    },
  },
});
</script>

<style lang="scss" scoped>
@import './styles.scss';
</style>
